import React from 'react'
import { MdEdit } from 'react-icons/md'
import { FaArrowRight, FaPlus } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { convertDateTimeFullFormatToLocal } from '../../../../utils/dateFormat/dateFormat'
import {
  CapabilityChange,
  ModifiedField,
  RoleActivityItem,
} from '../../../../interface/response/RoleActivityResponse'

const FIELD_LABEL_MAP: Record<string, string> = {
  RoleName: 'user_management.role_name',
  RoleDescription: 'user_management.role_description',
}

interface RoleActivityTimelineItemProps {
  item: RoleActivityItem
}

const RoleActivityTimelineItem: React.FC<RoleActivityTimelineItemProps> = ({
  item,
}) => {
  const { t } = useTranslation(['translation'])

  const getActorDetails = () => {
    const actor = item?.actor ?? item
    const userName = actor?.userName ?? ''
    const actorRole = actor?.actorRole ?? ''
    return actorRole ? `${userName} (${actorRole})` : userName
  }

  const getFieldLabel = (field: string) => {
    const key = FIELD_LABEL_MAP[field]
    return key ? t(key) : field
  }

  const getDisplayValue = (value: string) => {
    return value && value.trim().length > 0 ? value : t('common.none')
  }

  const modifiedFields = (item?.delta?.modified ?? []).filter(
    (mod: ModifiedField) => mod.from || mod.to,
  )

  const dotIcon =
    item?.actionType === 'DataAddition' ? (
      <FaPlus color="#fff" size={10} />
    ) : (
      <MdEdit color="#fff" size={10} />
    )

  const changes = item?.delta?.changes ?? []

  const groupedChanges = changes.reduce(
    (acc: Record<string, CapabilityChange[]>, change: CapabilityChange) => {
      const type = change.changeType
      if (!acc[type]) acc[type] = []
      acc[type].push(change)
      return acc
    },
    {},
  )

  const changeTypeColorClass = (changeType: string) => {
    if (changeType === 'Added') return 'text-success'
    if (changeType === 'Removed') return 'text-danger'
    return 'text-muted'
  }

  return (
    <div className="position-relative d-flex p-2">
      {/* Dot and vertical line */}
      <div
        className="d-flex flex-column align-items-center mt-1"
        style={{ width: '30px' }}
      >
        <div
          className={`rounded-circle d-flex justify-content-center align-items-center ${
            item?.actionType === 'DataAddition' ? 'bg-success' : 'bg-primary'
          }`}
          style={{
            width: '20px',
            height: '20px',
            zIndex: 2,
            backgroundClip: 'padding-box',
          }}
        >
          {dotIcon}
        </div>
        <div
          className="bg-light mt-2"
          style={{ width: 2, flexGrow: 1, zIndex: 1, opacity: 0.3 }}
        />
      </div>

      {/* Content block */}
      <div className="ms-3 flex-grow-1 w-100">
        <div className="d-flex flex-column w-100 gap-2">
          {/* Header row */}
          <div className="d-flex flex-column">
            <div>
              <strong className="me-1">{getActorDetails()}</strong>
              <span>{item?.heading}</span>
            </div>
            <small className="text-muted">
              {convertDateTimeFullFormatToLocal(item?.eventTimeStamp)}
            </small>
          </div>

          {/* Modified fields */}
          {modifiedFields.length > 0 && (
            <div className="d-flex flex-column gap-1">
              {modifiedFields.map((mod: ModifiedField, idx: number) => (
                <div
                  key={idx}
                  className="d-flex gap-1 align-items-center flex-wrap"
                >
                  <strong>{getFieldLabel(mod.field)}:</strong>
                  <span>{getDisplayValue(mod.from)}</span>
                  <span>
                    {<FaArrowRight size={15} className="text-primary" />}
                  </span>
                  <span>{getDisplayValue(mod.to)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Capability changes */}
          {changes.length > 0 && (
            <div className="d-flex flex-column gap-1">
              {Object.entries(groupedChanges).map(([changeType, items]) =>
                (items as CapabilityChange[]).map(
                  (change: CapabilityChange, idx: number) => (
                    <div
                      key={`${changeType}-${idx}`}
                      className="d-flex gap-1 align-items-center flex-wrap"
                    >
                      <strong className={changeTypeColorClass(changeType)}>
                        {changeType}:
                      </strong>
                      <span>{change.featureGroup}</span>
                      <span>
                        {<FaArrowRight size={15} className="text-primary" />}
                      </span>
                      <span>{change.feature}</span>
                      <span>
                        {<FaArrowRight size={15} className="text-primary" />}
                      </span>
                      <span>{change.capability}</span>
                    </div>
                  ),
                ),
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default RoleActivityTimelineItem
